import type { CreditGateUiType } from '@/lib/creditsTracking';

export const AI_CREDITS_REQUIRED_EVENT = 'ai-credits-required';
export const AI_CREDIT_NOT_ENOUGH_CODE = 'AI_CREDIT_NOT_ENOUGH';
export const AI_INSUFFICIENT_POINTS_CODE = 'INSUFFICIENT_POINTS';
export const AI_CREDIT_RECHARGE_ACTION = 'RECHARGE_POINTS';

export type CreditsRequiredEventDetail = {
  title?: string;
  copy?: string;
  productArea?: string;
  surface?: string;
  stage?: string;
  trigger?: string;
  uiType?: CreditGateUiType;
  source?: string;
  operation?: string;
  taskCode?: string | null;
  metadata?: Record<string, unknown>;
};

export type AiCreditsError = Error & {
  code?: typeof AI_CREDIT_NOT_ENOUGH_CODE | typeof AI_INSUFFICIENT_POINTS_CODE;
  action?: typeof AI_CREDIT_RECHARGE_ACTION;
};

export const createAiCreditsError = (
  message = 'AI credits are not enough. Recharge points before continuing.',
): AiCreditsError => {
  const error = new Error(message) as AiCreditsError;
  error.code = AI_CREDIT_NOT_ENOUGH_CODE;
  error.action = AI_CREDIT_RECHARGE_ACTION;
  return error;
};

export const isAiCreditsError = (error: unknown): error is AiCreditsError => {
  if (!(error instanceof Error)) return false;
  const typedError = error as Error & {
    code?: string;
    errorCode?: string;
    action?: string;
    apiError?: {
      errorCode?: unknown;
      payload?: { errorCode?: unknown };
    };
  };
  const code = String(
    typedError.code ||
      typedError.errorCode ||
      typedError.apiError?.errorCode ||
      typedError.apiError?.payload?.errorCode ||
      '',
  ).trim();

  return code === AI_CREDIT_NOT_ENOUGH_CODE || code === AI_INSUFFICIENT_POINTS_CODE;
};

export const isCreditsRequiredMessage = (message?: string | null) => {
  const normalized = String(message || '').toLowerCase();
  if (!normalized.trim()) return false;

  return (
    normalized.includes('insufficient credits') ||
    normalized.includes('insufficient_points') ||
    normalized.includes('ai_credit_not_enough') ||
    normalized.includes('not enough credits') ||
    normalized.includes('credits required') ||
    normalized.includes('credits to generate this page') ||
    normalized.includes('credits are not enough') ||
    normalized.includes('recharge points') ||
    (normalized.includes('top up') && normalized.includes('credits'))
  );
};

export const notifyAiCreditsRequired = (detail: CreditsRequiredEventDetail = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<CreditsRequiredEventDetail>(AI_CREDITS_REQUIRED_EVENT, {
      detail,
    }),
  );
};
